import { ArrowUpRight, Building07 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import type { SortDescriptor } from "react-aria-components";
import { MerchantLogo } from "@/components/application/merchant-logo/merchant-logo";
import { UnboxTable } from "@/components/application/table/unbox-table";
import type { UnboxTableColumn } from "@/components/application/table/unbox-table.types";
import { cx } from "@/utils/cx";
import { formatComparisonTip, formatCountCompact, formatMoneyCompact, formatPercentValue } from "../lib/format";
import {
  convertedValue,
  type MerchantRow,
  MONEY_METRICS,
  type SortKey,
  type SortState,
  sortRows,
  type TableMetric,
} from "../lib/merchant-rows";
import type { ChangeUnit } from "../lib/metrics";
import { regionCode } from "../lib/regions";
import { ChangeLine, RegionBadge } from "./parts";

const VISIBLE_ROWS = 10;
const FLASH_MS = 2400;
const FLASH_CLASS = "animate-[row-flash_0.8s_ease-in-out_0.3s_2]";

export type RowHighlight = { merchantId: string; nonce: number };

type MerchantTableProps = {
  rows: MerchantRow[];
  totalsCurrency: string;
  sort: SortState;
  onSort: (key: SortKey) => void;
  highlight: RowHighlight | null;
  collapseKey: string;
  onOpenMerchant: (merchantId: string) => void;
};

const NoteCell = ({ value, note }: { value: string; note: string }) => (
  <span className="flex flex-col gap-0.5 whitespace-nowrap tabular-nums">
    <span className="font-medium text-[#21133a]">{value}</span>
    <span className="text-[13px] leading-[18px] text-[#21133a]/60">{note}</span>
  </span>
);

const EmptyCell = ({ label = "—" }: { label?: string }) => <span className="text-[#21133a]/60">{label}</span>;

const metricColumn = (
  key: TableMetric,
  label: string,
  format: (value: number, row: MerchantRow) => string,
  totalsCurrency: string,
  unit: ChangeUnit = "percent",
): UnboxTableColumn<MerchantRow> => ({
  id: key,
  label,
  minWidth: "112px",
  sortable: true,
  render: (_value, row) => {
    const reading = row.metrics[key];
    const converted = convertedValue(row, key);
    const tip =
      MONEY_METRICS.includes(key) && row.currency !== totalsCurrency && converted !== null
        ? formatComparisonTip(converted, totalsCurrency)
        : undefined;
    return (
      <span className="flex flex-col gap-0.5 whitespace-nowrap tabular-nums">
        <span title={tip} className="font-medium text-[#21133a]">
          {reading.value === null ? "—" : format(reading.value, row)}
        </span>
        <ChangeLine change={reading.change} unit={unit} />
      </span>
    );
  },
});

const merchantColumns = (totalsCurrency: string): UnboxTableColumn<MerchantRow>[] => {
  const money = (value: number, row: MerchantRow) => formatMoneyCompact(value, row.currency);
  const count = (value: number) => formatCountCompact(value);

  return [
    {
      id: "merchant",
      label: "Merchant",
      minWidth: "220px",
      render: (_value, row) => (
        <span data-merchant-row={row.merchantId} className="flex min-w-0 items-center gap-3">
          <MerchantLogo name={row.name} logoUrl={row.logoUrl} size="sm" className="size-9 rounded-md" />
          <span className="min-w-0 truncate font-medium text-[#21133a]">{row.name}</span>
          <RegionBadge regionKey={row.regionKey} />
          <ArrowUpRight
            width={16}
            height={16}
            aria-hidden="true"
            className="shrink-0 text-[#21133a]/60 opacity-0 transition-opacity group-hover:opacity-100"
          />
        </span>
      ),
    },
    metricColumn("affiliateGmv", "Affiliate GMV", money, totalsCurrency),
    {
      id: "shareOfRegion",
      label: "Share of region",
      minWidth: "112px",
      sortable: true,
      render: (_value, row) =>
        row.shareOfRegion === null ? (
          <EmptyCell />
        ) : (
          <NoteCell value={`${Math.round(row.shareOfRegion)}%`} note={`of ${regionCode(row.regionKey)}`} />
        ),
    },
    {
      id: "pendingReviews",
      label: "Brand safety",
      minWidth: "112px",
      sortable: true,
      render: (_value, row) => {
        if (row.pendingReviews === null) return <EmptyCell />;
        if (row.pendingReviews === 0) return <EmptyCell label="All clear" />;
        return <NoteCell value={String(row.pendingReviews)} note="need action" />;
      },
    },
    metricColumn("videos", "Videos posted", count, totalsCurrency),
    metricColumn("views", "Video views", count, totalsCurrency),
    metricColumn(
      "videoConversionRate",
      "Video conv. rate",
      (value) => formatPercentValue(value, 1),
      totalsCurrency,
      "points",
    ),
    metricColumn("activeCreators", "Active creators", count, totalsCurrency),
    metricColumn("gmvPerVideo", "GMV per video", money, totalsCurrency),
    metricColumn("gmvPerSample", "GMV per sample", money, totalsCurrency),
  ];
};

const toDescriptor = (sort: SortState) => ({
  column: sort.key,
  direction: sort.direction === "desc" ? ("descending" as const) : ("ascending" as const),
});

export function MerchantTable({
  rows,
  totalsCurrency,
  sort,
  onSort,
  highlight,
  collapseKey,
  onOpenMerchant,
}: MerchantTableProps) {
  const [expanded, setExpanded] = useState(false);
  const [flashing, setFlashing] = useState<RowHighlight | null>(null);

  const sorted = sortRows(rows, sort);
  const visible = expanded ? sorted : sorted.slice(0, VISIBLE_ROWS);

  useEffect(() => {
    setExpanded(false);
  }, [collapseKey]);

  useEffect(() => {
    if (!highlight) {
      setFlashing(null);
      return;
    }
    setFlashing(highlight);
    const timer = setTimeout(() => setFlashing(null), FLASH_MS);
    return () => clearTimeout(timer);
  }, [highlight]);

  const targetIndex = flashing ? sorted.findIndex((row) => row.merchantId === flashing.merchantId) : -1;

  useEffect(() => {
    if (!flashing || targetIndex < 0) return;
    if (targetIndex >= VISIBLE_ROWS && !expanded) {
      setExpanded(true);
      return;
    }
    const element = document.querySelector(`[data-merchant-row="${CSS.escape(flashing.merchantId)}"]`);
    element?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [flashing, targetIndex, expanded]);

  return (
    <section id="overview-merchants" className="flex scroll-mt-24 flex-col">
      <UnboxTable
        aria-label="Merchant summary"
        columns={merchantColumns(totalsCurrency)}
        rows={visible}
        rowId="merchantId"
        rowClassName={(row) => cx("group", flashing?.merchantId === row.merchantId && FLASH_CLASS)}
        onClickRow={(row) => onOpenMerchant(row.merchantId)}
        sorting={{
          descriptor: toDescriptor(sort),
          onSortChange: (descriptor: SortDescriptor) => onSort(descriptor.column as SortKey),
        }}
        emptyState={{ icon: Building07, title: "There are no merchants to show yet" }}
      />
      {sorted.length > VISIBLE_ROWS && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
          className="mt-3 cursor-pointer self-center px-5 py-2 text-sm text-[#21133a]/75 hover:text-[#21133a]"
        >
          {expanded ? "Show fewer" : `Show all ${sorted.length} merchants`}
        </button>
      )}
    </section>
  );
}
